import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import Drawer from '../../../components/ui/Drawer';
import TabBar from '../../../components/TabBar';
import { useDrawerResize } from '../../../hooks/useDrawerResize';
import NotesPanel from '../../../components/common/NotesPanel';
import { useResolvedAccessLevel } from '../../../components/PermissionsContainer';
import safetySystemService from '../../../services/safetySystem.service';
import type {
  SafetyDocument, SafetyDocumentTable, UnitChildKey, UnitChildren, UnitChildrenSaveBody, UnitDetail, UnitPayroll, UnitPayrollReveal,
} from '../../../types/safetySystem';
import { emptyUnitChildLookups } from '../../../types/safetySystem';
import SafetyDocuments from './SafetyDocuments';
import UnitDriverTab from './UnitDriverTab';
import UnitTruckTab from './UnitTruckTab';
import UnitCertTab from './UnitCertTab';
import UnitPayrollTab from './UnitPayrollTab';
import UnitRetentionTab from './UnitRetentionTab';
import UnitNotificationsTab from './UnitNotificationsTab';
import { DiscardFooter, ResizeHandle, SafetyHistoryTab, errorText } from './safetyDrawerShared';
import { ChildRow, childrenDirty, withKeys } from './ticketForm';
import {
  CERT_FIELDS, CHILD_FIELDS, CHILD_KEYS, CHILD_KEYS_BY_TAB, RETENTION_FIELDS, UnitSaveErrors, childSavePlan, diffChanges,
  emptyUnitSaveErrors, isDirty, lookupLabel, mapUnitSaveErrors, payrollFormFrom, pickFields,
} from './unitForm';

/**
 * Unit drawer — port of the V1 Safety unit page. Tabs: Driver, Truck, Payroll, Cert,
 * Retention, Documents, Notes, History (V1 merges unit_history / driver_history /
 * truck_history), Notifications (the e-mails logged against the unit and its current
 * owner — V1 `viewemailnotification`). V1 has no Employment / References tab on the unit.
 *
 * Permissions: `safety-system-unit-edit` >= 2 enables the inputs and the Save button;
 * `safety-system-payroll-sensitive` >= 2 reveals the SSN Edit control and the bank-number
 * Reveal / Edit controls. Both default to hidden-level (0) when un-granted, so non-granted
 * users get a read-only drawer with no sensitive editing.
 *
 * Data: GET /units/{r} on open; GET /units/{r}/children (nine child lists + Cert/Retention
 * scalars + pick lists) the first time a tab with grids opens; GET /units/{r}/payroll the
 * first time the Payroll tab opens. Both are cached for the open.
 *
 * Save (any save tab's button) runs every dirty step IN ORDER — driver, truck, truck
 * children (OW permits / ELD), cert, cert children, payroll, payroll children, retention,
 * retention children — each posting only its diff, stopping at the first failure. Steps
 * that already committed are re-baselined so a retry re-sends only what is still unsaved;
 * the drawer never closes while something is dirty. A committed children step whose
 * re-read then fails is frozen (`savedNotReloaded`): its new rows have no recnos yet, so a
 * retry must not re-post them; the user reopens the unit to keep editing those lists.
 */

export type UnitDrawerTab = 'driver' | 'truck' | 'payroll' | 'cert' | 'retention' | 'documents' | 'notes' | 'history' | 'notifications';

export interface UnitDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  unitrecno: number | null;
  onSaved: () => void;
  /** Tab shown when the drawer opens / the unit changes (Units page view → owning tab). Default `driver`. */
  initialTab?: UnitDrawerTab;
}

type TabId = UnitDrawerTab;
const SAVE_TABS: TabId[] = ['driver', 'truck', 'payroll', 'cert', 'retention'];
/** Tabs that render child-row grids (trigger the lazy GET /children). */
const CHILD_TABS: TabId[] = ['truck', 'payroll', 'cert', 'retention'];
/** `*_history.dbtable` -> label. `owner` rows live in unit_history (360 addition; V1 writes none). */
const UNIT_HISTORY_LABELS: Record<string, string> = {
  unit: 'Unit', driver: 'Driver', truck: 'Truck', owner: 'Owner', notes: 'Note', restriction: 'Restriction', permit: 'Permit',
  owpermit: 'OW Permit', drivertraining: 'Corrective Action', eldinfo: 'ELD', insurance: 'Insurance', payrolldeduction: 'Deduction',
  survey: 'Survey', award: 'Award',
};
/** Which V1 history table a merged row came from. */
const UNIT_HISTORY_SOURCES: Record<string, string> = { unit: 'Unit', driver: 'Driver', truck: 'Truck' };
/** Child list key -> its `UNIT_HISTORY_LABELS` entry (for the saved-not-reloaded notice). */
const CHILD_LIST_LABEL_KEYS: Record<UnitChildKey, string> = {
  restrictions: 'restriction', permits: 'permit', owpermits: 'owpermit', training: 'drivertraining', eld: 'eldinfo',
  insurance: 'insurance', deductions: 'payrolldeduction', surveys: 'survey', awards: 'award',
};
const PHOTO_TYPES = new Set(['PHOTO', 'DRIVER PROFILE PHOTO']);
const DOCUMENT_TABLES: { id: SafetyDocumentTable; label: string }[] = [
  { id: 'driver', label: 'Driver' },
  { id: 'truck', label: 'Truck' },
  { id: 'unit', label: 'Unit' },
];
const NO_LOOKUPS = emptyUnitChildLookups();

/** Which recno the Documents tab attaches to for the selected table (null = no linked row). */
const documentRecno = (detail: UnitDetail, table: SafetyDocumentTable, unitrecno: number | null): number | null => {
  const raw = table === 'driver' ? detail.driver?.recno : table === 'truck' ? detail.truck?.recno : (detail.unit?.recno ?? unitrecno);
  return raw ? Number(raw) : null;
};

const statusChipClass = (status: string): string => {
  switch (status) {
    case 'OUT OF SERVICE': return 'bg-red-600 text-white';
    case 'TERMINATED': return 'bg-gray-900 text-white dark:bg-black';
    default: return 'bg-accent text-accent-fg';
  }
};

const rowsFrom = (c: UnitChildren): Partial<Record<UnitChildKey, ChildRow[]>> => {
  const out: Partial<Record<UnitChildKey, ChildRow[]>> = {};
  for (const key of CHILD_KEYS) out[key] = withKeys(c[key] ?? []);
  return out;
};

interface SaveStep {
  run: () => Promise<unknown>;
  /** After this step committed but a later one failed: make a retry skip it. */
  rebaseline: () => Promise<void>;
}


export default function UnitDrawer({ isOpen, onClose, unitrecno, onSaved, initialTab = 'driver' }: UnitDrawerProps) {
  // Read through a ref so a parent re-rendering with a different initialTab never re-fetches the unit.
  const initialTabRef = useRef<TabId>(initialTab);
  initialTabRef.current = initialTab;
  const canEdit = useResolvedAccessLevel('safety-system-unit-edit', true) >= 2;
  // Drag-to-resize with snap-to-nav, like the Pro detail drawer. 1200px was the
  // old fixed width, so nothing moves until someone grabs the handle.
  const { width, onHandleMouseDown, onHandleDoubleClick } = useDrawerResize({
    isOpen, storageKey: 'safetyUnitDrawerWidth', minWidth: 480, defaultWidth: 1200,
  });
  const canSensitive = useResolvedAccessLevel('safety-system-payroll-sensitive', true) >= 2;
  const { accounts } = useMsal();
  const currentUser = accounts[0]?.username || accounts[0]?.name || '';

  const [detail, setDetail] = useState<UnitDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [activeTab, setActiveTab] = useState<TabId>('driver');
  const [documentTable, setDocumentTable] = useState<SafetyDocumentTable>('driver');
  const [noteCount, setNoteCount] = useState(0);

  // Editable copies + the pristine originals they are diffed against on Save.
  const [driver, setDriver] = useState<Record<string, any>>({});
  const [unit, setUnit] = useState<Record<string, any>>({});
  const [ssn, setSsn] = useState<string | null>(null); // null = not editing (masked)
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  // Truck tab: truck + owner records, the owner's masked Tax ID, and a pending owner switch.
  const [truck, setTruck] = useState<Record<string, any>>({});
  const [owner, setOwner] = useState<Record<string, any>>({});
  const [ownerSsn, setOwnerSsn] = useState<string | null>(null);
  const [ownerrecno, setOwnerrecno] = useState('');
  const [truckPhotoUrl, setTruckPhotoUrl] = useState<string | null>(null);

  // Child lists (lazy, cached per open) + the Cert / Retention scalar copies they carry.
  const [children, setChildren] = useState<UnitChildren | null>(null);
  const [childrenLoading, setChildrenLoading] = useState(false);
  const [childrenError, setChildrenError] = useState('');
  const [childRows, setChildRows] = useState<Partial<Record<UnitChildKey, ChildRow[]>>>({});
  // Lists whose save committed but whose re-read failed: excluded from dirty / the next save.
  const [savedNotReloaded, setSavedNotReloaded] = useState<UnitChildKey[]>([]);
  const [cert, setCert] = useState<Record<string, any>>({});
  const [certBase, setCertBase] = useState<Record<string, any>>({});
  const [retention, setRetention] = useState<Record<string, any>>({});
  const [retentionBase, setRetentionBase] = useState<Record<string, any>>({});
  // Payroll tab (lazy, cached per open).
  const [payroll, setPayroll] = useState<UnitPayroll | null>(null);
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [payrollError, setPayrollError] = useState('');
  const [payrollForm, setPayrollForm] = useState<Record<string, any>>({});
  const [payrollBase, setPayrollBase] = useState<Record<string, any>>({});
  // Bank numbers to post: digits, or '' for an explicit Clear. `bankEditsVersion` re-keys the
  // tab's bank inputs whenever this is re-baselined to {} so no typed text survives.
  const [bankEdits, setBankEdits] = useState<Record<string, string>>({});
  const [bankEditsVersion, setBankEditsVersion] = useState(0);
  const [editingBank, setEditingBank] = useState(false);
  const [revealed, setRevealed] = useState<UnitPayrollReveal | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [revealError, setRevealError] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveErrors, setSaveErrors] = useState<UnitSaveErrors>(emptyUnitSaveErrors());
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (isOpen && contentRef.current) setPortalContainer(contentRef.current.closest('dialog') || null);
  }, [isOpen, detail]);

  const resetForm = useCallback((d: UnitDetail | null) => {
    setDriver(d ? { ...d.driver } : {});
    setUnit(d ? { ...d.unit } : {});
    setSsn(null);
    setTruck(d ? { ...d.truck } : {});
    setOwner(d ? { ...d.owner } : {});
    setOwnerSsn(null);
    setOwnerrecno('');
    setSaveError('');
    setSaveErrors(emptyUnitSaveErrors());
    setConfirmDiscard(false);
  }, []);

  const resetBankEdits = useCallback(() => {
    setBankEdits({});
    setBankEditsVersion((v) => v + 1);
    setEditingBank(false);
  }, []);

  const applyChildren = useCallback((c: UnitChildren) => {
    setChildren(c);
    setChildRows(rowsFrom(c));
    setSavedNotReloaded([]);
    const certFields = pickFields(c.driver, CERT_FIELDS);
    const retentionFields = pickFields(c.driver, RETENTION_FIELDS);
    setCert(certFields);
    setCertBase(certFields);
    setRetention(retentionFields);
    setRetentionBase(retentionFields);
  }, []);

  const applyPayroll = useCallback((p: UnitPayroll) => {
    setPayroll(p);
    const form = payrollFormFrom(p);
    setPayrollForm(form);
    setPayrollBase(form);
    resetBankEdits();
  }, [resetBankEdits]);

  // Lazy loads are requested once per open; `lazyGen` invalidates an in-flight response
  // when the drawer moves to another unit (no AbortController: the effects must not
  // cancel themselves on their own state changes).
  const lazyGen = useRef(0);
  const childrenRequested = useRef(false);
  const payrollRequested = useRef(false);

  const resetLazy = useCallback(() => {
    lazyGen.current += 1;
    childrenRequested.current = false;
    payrollRequested.current = false;
    setChildren(null);
    setChildrenLoading(false);
    setChildrenError('');
    setChildRows({});
    setSavedNotReloaded([]);
    setCert({}); setCertBase({});
    setRetention({}); setRetentionBase({});
    setPayroll(null);
    setPayrollLoading(false);
    setPayrollError('');
    setPayrollForm({}); setPayrollBase({});
    resetBankEdits();
    setRevealed(null);
    setRevealError('');
  }, [resetBankEdits]);

  // Load on open / unit change.
  useEffect(() => {
    resetLazy();
    if (!isOpen || !unitrecno) {
      setDetail(null);
      resetForm(null);
      setPhotoUrl(null);
      setTruckPhotoUrl(null);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setLoadError('');
    setActiveTab(initialTabRef.current);
    setDocumentTable('driver');
    setNoteCount(0);
    safetySystemService.getUnit(unitrecno, controller.signal)
      .then((d) => {
        if (controller.signal.aborted) return;
        setDetail(d);
        resetForm(d);
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        setDetail(null);
        setLoadError(e?.response?.data?.detail || 'Unable to load this unit.');
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [isOpen, unitrecno, resetForm, resetLazy]);

  // Child lists: first time a grid tab opens (one fetch per open; a failure shows in the grids).
  useEffect(() => {
    if (!isOpen || !unitrecno || !detail || !CHILD_TABS.includes(activeTab) || childrenRequested.current) return;
    childrenRequested.current = true;
    const gen = lazyGen.current;
    const live = () => gen === lazyGen.current;
    setChildrenLoading(true);
    safetySystemService.getUnitChildren(unitrecno)
      .then((c) => { if (live()) applyChildren(c); })
      .catch((e) => { if (live()) setChildrenError(e?.response?.data?.detail || 'Unable to load the unit child records.'); })
      .finally(() => { if (live()) setChildrenLoading(false); });
  }, [isOpen, unitrecno, detail, activeTab, applyChildren]);

  // Payroll block: first time the Payroll tab opens.
  useEffect(() => {
    if (!isOpen || !unitrecno || !detail || activeTab !== 'payroll' || payrollRequested.current) return;
    payrollRequested.current = true;
    const gen = lazyGen.current;
    const live = () => gen === lazyGen.current;
    setPayrollLoading(true);
    safetySystemService.getUnitPayroll(unitrecno)
      .then((p) => { if (live()) applyPayroll(p); })
      .catch((e) => { if (live()) setPayrollError(e?.response?.data?.detail || 'Unable to load the payroll tab.'); })
      .finally(() => { if (live()) setPayrollLoading(false); });
  }, [isOpen, unitrecno, detail, activeTab, applyPayroll]);

  // Revealed bank numbers live only while the Payroll tab is on screen.
  useEffect(() => {
    if (activeTab !== 'payroll') { setRevealed(null); setRevealError(''); }
  }, [activeTab]);

  // Header photo: newest PHOTO / DRIVER PROFILE PHOTO document on the driver.
  useEffect(() => {
    const driverrecno = detail?.driver?.recno;
    setPhotoUrl(null);
    if (!driverrecno) return;
    let cancelled = false;
    safetySystemService.listDocuments('driver', Number(driverrecno))
      .then((res) => {
        if (cancelled) return;
        const photo = (res?.data ?? []).find((doc: SafetyDocument) => PHOTO_TYPES.has(String(doc.documenttype).toUpperCase()));
        setPhotoUrl(photo?.url || null);
      })
      .catch(() => { /* photo is decorative; the drawer still works without it */ });
    return () => { cancelled = true; };
  }, [detail]);

  // Truck photo: newest PHOTO document on the truck (same approach as the driver photo).
  useEffect(() => {
    const truckrecno = detail?.truck?.recno;
    setTruckPhotoUrl(null);
    if (!truckrecno) return;
    let cancelled = false;
    safetySystemService.listDocuments('truck', Number(truckrecno))
      .then((res) => {
        if (cancelled) return;
        const photo = (res?.data ?? []).find((doc: SafetyDocument) => PHOTO_TYPES.has(String(doc.documenttype).toUpperCase()));
        setTruckPhotoUrl(photo?.url || null);
      })
      .catch(() => { /* decorative */ });
    return () => { cancelled = true; };
  }, [detail]);

  const ownerSwitched = !!detail && ownerrecno !== '' && ownerrecno !== String(detail.owner?.recno ?? '');
  const childDirty = useCallback(
    (key: UnitChildKey) => childrenDirty(children?.[key] ?? [], childRows[key] ?? [], CHILD_FIELDS[key]),
    [children, childRows],
  );

  const childSaveable = useCallback((key: UnitChildKey) => !savedNotReloaded.includes(key), [savedNotReloaded]);

  const dirty = useMemo(() => {
    if (!detail) return false;
    return isDirty(detail.driver, driver) || isDirty(detail.unit, unit) || (ssn !== null && ssn !== '')
      || isDirty(detail.truck, truck) || isDirty(detail.owner, owner) || (ownerSsn !== null && ownerSsn !== '') || ownerSwitched
      || isDirty(certBase, cert) || isDirty(retentionBase, retention) || isDirty(payrollBase, payrollForm)
      || Object.keys(bankEdits).length > 0 || CHILD_KEYS.some((k) => childSaveable(k) && childDirty(k));
  }, [detail, driver, unit, ssn, truck, owner, ownerSsn, ownerSwitched, certBase, cert, retentionBase, retention, payrollBase, payrollForm, bankEdits, childDirty, childSaveable]);

  const touched = () => { setSaveError(''); setSaveErrors((p) => (Object.keys(p.fields).length || Object.keys(p.rows).length ? emptyUnitSaveErrors() : p)); };
  const onDriverChange = (key: string, value: unknown) => { setDriver((p) => ({ ...p, [key]: value })); touched(); };
  const onUnitChange = (key: string, value: unknown) => { setUnit((p) => ({ ...p, [key]: value })); touched(); };
  const onTruckChange = (key: string, value: unknown) => { setTruck((p) => ({ ...p, [key]: value })); touched(); };
  const onOwnerChange = (key: string, value: unknown) => { setOwner((p) => ({ ...p, [key]: value })); touched(); };
  const onCertChange = (key: string, value: unknown) => { setCert((p) => ({ ...p, [key]: value })); touched(); };
  const onRetentionChange = (key: string, value: unknown) => { setRetention((p) => ({ ...p, [key]: value })); touched(); };
  const onPayrollFormChange = (key: string, value: unknown) => { setPayrollForm((p) => ({ ...p, [key]: value })); touched(); };
  // A bank field typed and then emptied is NOT posted (the number on file stays); only the
  // explicit Clear control (`onBankClear`) sets the '' marker that blanks it on save.
  const onBankEdit = (key: string, digits: string) => {
    setBankEdits((p) => {
      const next = { ...p };
      if (digits === '') delete next[key];
      else next[key] = digits;
      return next;
    });
    touched();
  };
  const onBankClear = (key: string) => { setBankEdits((p) => ({ ...p, [key]: '' })); touched(); };
  const onChildRowsChange = (key: UnitChildKey, rows: ChildRow[]) => { setChildRows((p) => ({ ...p, [key]: rows })); touched(); };
  // Switching owner discards edits to the old owner's fields — they would otherwise be
  // written to a record the unit no longer points at.
  const onOwnerRecnoChange = (value: string) => {
    setOwnerrecno(value);
    setOwner(detail ? { ...detail.owner } : {});
    setOwnerSsn(null);
    touched();
  };

  const handleReveal = async () => {
    if (!unitrecno) return;
    setRevealing(true);
    setRevealError('');
    try {
      setRevealed(await safetySystemService.revealUnitPayroll(unitrecno));
    } catch (e) {
      setRevealError(errorText(e));
    } finally {
      setRevealing(false);
    }
  };

  /**
   * After the Driver step committed but a later step failed: re-baseline the
   * driver/unit state on the server copy (falling back to the just-saved values) so
   * a retry re-sends ONLY what is still unsaved. Other tabs' edits are kept as they are.
   */
  const rebaselineDriver = async (recno: number) => {
    try {
      const d = await safetySystemService.getUnit(recno);
      setDetail(d);
      setDriver({ ...d.driver });
      setUnit({ ...d.unit });
    } catch {
      setDetail((prev) => (prev ? { ...prev, driver: { ...prev.driver, ...driver }, unit: { ...prev.unit, ...unit } } : prev));
    }
    setSsn(null);
  };

  const rebaselineTruck = async (recno: number) => {
    try {
      const d = await safetySystemService.getUnit(recno);
      setDetail((prev) => (prev ? { ...prev, truck: d.truck, owner: d.owner, unit: { ...prev.unit, ownerrecno: d.unit?.ownerrecno } } : d));
      setTruck({ ...d.truck });
      setOwner({ ...d.owner });
    } catch {
      setDetail((prev) => (prev ? { ...prev, truck: { ...prev.truck, ...truck }, owner: { ...prev.owner, ...owner } } : prev));
    }
    setOwnerrecno('');
    setOwnerSsn(null);
  };

  /**
   * Re-read the saved child lists (new rows need their recnos) without touching the other
   * lists' edits. When the re-read fails the lists are frozen (`savedNotReloaded`): their new
   * rows still carry no recno, so a retry would INSERT them a second time. Returns whether
   * the re-read succeeded.
   */
  const rebaselineChildren = async (recno: number, keys: UnitChildKey[]): Promise<boolean> => {
    try {
      const fresh = await safetySystemService.getUnitChildren(recno);
      setChildren((prev) => {
        if (!prev) return fresh;
        const next = { ...prev };
        for (const k of keys) next[k] = fresh[k] ?? [];
        return next;
      });
      setChildRows((prev) => {
        const next = { ...prev };
        for (const k of keys) next[k] = withKeys(fresh[k] ?? []);
        return next;
      });
      return true;
    } catch {
      setSavedNotReloaded((prev) => Array.from(new Set([...prev, ...keys])));
      return false;
    }
  };

  /** The survey drawer saved: re-read only the surveys list; say so when that re-read fails. */
  const handleSurveysSaved = async () => {
    if (!unitrecno) return;
    const ok = await rebaselineChildren(unitrecno, ['surveys']);
    if (!ok) setSaveError('Survey saved, but the surveys list could not be refreshed — reopen the unit to see it.');
  };

  /**
   * Save every dirty step, not just the visible tab's: edits on the Driver tab must not be
   * dropped because the user clicked Save from the Payroll tab. Order: driver, truck (+ OW
   * permits / ELD), cert (+ restrictions / permits / training), payroll (+ insurance /
   * deductions), retention (+ surveys / awards); stop at the first failure.
   */
  const handleSave = async () => {
    if (!detail || !unitrecno || !SAVE_TABS.includes(activeTab)) return;
    const recno = unitrecno;
    const hasKeys = (o: Record<string, unknown>) => Object.keys(o).length > 0;
    const steps: SaveStep[] = [];
    const plans: Partial<Record<UnitChildKey, string[]>> = {};

    const driverBody: { driver: Record<string, any>; unit: Record<string, any> } = {
      driver: diffChanges(detail.driver, driver),
      unit: diffChanges(detail.unit, unit),
    };
    if (ssn !== null && ssn !== '') driverBody.driver.ssn = ssn;
    // Below payroll-sensitive 2 the DOB was never shown (server sends null), so never echo it back.
    if (!canSensitive) delete driverBody.driver.dateofbirth;
    if (hasKeys(driverBody.driver) || hasKeys(driverBody.unit)) {
      steps.push({ run: () => safetySystemService.saveDriverTab(recno, driverBody), rebaseline: () => rebaselineDriver(recno) });
    }

    const truckBody: { truck: Record<string, any>; owner: Record<string, any>; ownerrecno?: number } = {
      truck: diffChanges(detail.truck, truck),
      owner: ownerSwitched ? {} : diffChanges(detail.owner, owner),
    };
    if (!ownerSwitched && ownerSsn !== null && ownerSsn !== '') truckBody.owner.truckownerssn = ownerSsn;
    if (ownerSwitched) truckBody.ownerrecno = Number(ownerrecno);
    if (hasKeys(truckBody.truck) || hasKeys(truckBody.owner) || ownerSwitched) {
      steps.push({ run: () => safetySystemService.saveTruckTab(recno, truckBody), rebaseline: () => rebaselineTruck(recno) });
    }

    const childStep = (keys: UnitChildKey[]) => {
      const body: UnitChildrenSaveBody = {};
      for (const key of keys) {
        if (!childSaveable(key)) continue;            // committed earlier but never reloaded: never re-post
        const plan = childSavePlan(children?.[key] ?? [], childRows[key] ?? [], CHILD_FIELDS[key]);
        if (plan.rows.length) { body[key] = plan.rows; plans[key] = plan.keys; }
      }
      const posted = Object.keys(body) as UnitChildKey[];
      if (!posted.length) return;
      steps.push({ run: () => safetySystemService.saveUnitChildren(recno, body), rebaseline: async () => { await rebaselineChildren(recno, posted); } });
    };
    childStep(CHILD_KEYS_BY_TAB.truck);

    const certDiff = diffChanges(certBase, cert);
    if (hasKeys(certDiff)) {
      steps.push({ run: () => safetySystemService.saveUnitCert(recno, { driver: certDiff }), rebaseline: async () => setCertBase({ ...cert }) });
    }
    childStep(CHILD_KEYS_BY_TAB.cert);

    const payrollDiff = { ...diffChanges(payrollBase, payrollForm), ...bankEdits };
    if (hasKeys(payrollDiff)) {
      steps.push({
        run: () => safetySystemService.saveUnitPayroll(recno, { unit: payrollDiff }),
        rebaseline: async () => { setPayrollBase({ ...payrollForm }); resetBankEdits(); },
      });
    }
    childStep(CHILD_KEYS_BY_TAB.payroll);

    const retentionDiff = diffChanges(retentionBase, retention);
    if (hasKeys(retentionDiff)) {
      steps.push({ run: () => safetySystemService.saveUnitRetention(recno, { driver: retentionDiff }), rebaseline: async () => setRetentionBase({ ...retention }) });
    }
    childStep(CHILD_KEYS_BY_TAB.retention);

    if (!steps.length) {
      onClose();
      return;
    }
    setSaving(true);
    setSaveError('');
    setSaveErrors(emptyUnitSaveErrors());
    const committed: SaveStep[] = [];
    try {
      for (const step of steps) {
        await step.run();
        committed.push(step);
      }
      onSaved();
      onClose();
    } catch (e: any) {
      if (committed.length) {
        onSaved(); // the earlier steps are committed: let the grid reflect them
        for (const step of committed) await step.rebaseline();
      }
      // The truck save's VIN-dupe 409 carries `{ message, dupes: [{recno, unitnumber}] }`: name the units.
      setSaveError(errorText(e, { withDupeUnits: true }));
      setSaveErrors(mapUnitSaveErrors(e?.response?.data?.detail, plans) ?? emptyUnitSaveErrors());
    } finally {
      setSaving(false);
    }
  };

  // Veto user-initiated closes (X / Esc / backdrop / Cancel) while dirty; the footer
  // then shows an inline "Discard changes?" confirmation instead of a browser dialog.
  const requestClose = (): boolean => {
    if (dirty) { setConfirmDiscard(true); return false; }
    return true;
  };
  const handleCancel = () => { if (requestClose()) onClose(); };
  const handleDiscard = () => { resetForm(detail); onClose(); };

  const statusLabel = String(unit.status ?? detail?.unit?.status ?? '').toUpperCase();
  const oosLabel = statusLabel === 'OUT OF SERVICE' ? lookupLabel(detail?.lookups?.outofservicetypes, unit.outofservicetype) : '';
  const title = detail ? (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-lg font-semibold">{`${detail.unit.unitnumber ?? ''} - ${driver.firstname ?? ''} ${driver.lastname ?? ''}`.trim()}</span>
      {statusLabel && (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusChipClass(statusLabel)}`}>
          {statusLabel}{oosLabel ? ` - ${oosLabel}` : ''}
        </span>
      )}
    </div>
  ) : (loading ? 'Loading unit…' : 'Unit');

  const tabs = [
    { id: 'driver', label: 'Driver', current: activeTab === 'driver' },
    { id: 'truck', label: 'Truck', current: activeTab === 'truck' },
    { id: 'payroll', label: 'Payroll', current: activeTab === 'payroll' },
    { id: 'cert', label: 'Cert', current: activeTab === 'cert' },
    { id: 'retention', label: 'Retention', current: activeTab === 'retention' },
    { id: 'documents', label: 'Documents', current: activeTab === 'documents' },
    { id: 'notes', label: 'Notes', current: activeTab === 'notes', ...(noteCount > 0 ? { badge: noteCount } : {}) },
    // History / Notifications read by unit recno; no recno (drawer opened on nothing) -> no tab at all.
    ...(unitrecno ? [
      { id: 'history', label: 'History', current: activeTab === 'history' },
      { id: 'notifications', label: 'Notifications', current: activeTab === 'notifications' },
    ] : []),
  ];

  const footer = confirmDiscard ? (
    <DiscardFooter onDiscard={handleDiscard} onKeep={() => setConfirmDiscard(false)} />
  ) : (
    <div className="flex items-center justify-end gap-2">
      <button type="button" onClick={handleCancel} className="rounded-md border border-rule px-3 py-1.5 text-sm hover:bg-highlight">Cancel</button>
      {canEdit && detail && SAVE_TABS.includes(activeTab) && (
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || loading}
          className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      )}
    </div>
  );

  const childListProps = {
    childRows,
    onChildRowsChange,
    lookups: children?.lookups ?? NO_LOOKUPS,
    childrenLoading,
    childrenError,
    rowErrors: saveErrors.rows,
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      onBeforeClose={requestClose}
      title={title}
      maxWidth="full"
      width={`${width}px`}
      contentClassName="relative flex-1 overflow-y-auto px-4 py-4 sm:px-6"
      footer={footer}
    >
      {/* Width is remembered per user in localStorage (useDrawerResize). */}
      <ResizeHandle width={width} onHandleMouseDown={onHandleMouseDown} onHandleDoubleClick={onHandleDoubleClick} />
      <div ref={contentRef}>
        <TabBar tabs={tabs} onTabChange={(id) => setActiveTab(id as TabId)} compact noBorder className="mb-4" />

        {loadError && <div role="alert" className="mb-3 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{loadError}</div>}
        {saveError && <div role="alert" className="mb-3 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{saveError}</div>}
        {savedNotReloaded.length > 0 && (
          <div role="status" className="mb-3 rounded-md border border-rule bg-highlight px-3 py-2 text-sm text-muted">
            Saved, but could not reload — reopen the unit to continue editing these rows
            {` (${savedNotReloaded.map((k) => UNIT_HISTORY_LABELS[CHILD_LIST_LABEL_KEYS[k]] ?? k).join(', ')}).`}
          </div>
        )}

        {loading && <div className="py-8 text-center text-sm text-muted">Loading unit…</div>}

        {!loading && detail && activeTab === 'driver' && (
          <UnitDriverTab
            detail={detail}
            driver={driver}
            unit={unit}
            onDriverChange={onDriverChange}
            onUnitChange={onUnitChange}
            canEdit={canEdit}
            canSensitive={canSensitive}
            ssn={ssn}
            onSsnChange={(v) => { setSsn(v); touched(); }}
            photoUrl={photoUrl}
            portalContainer={portalContainer}
          />
        )}
        {!loading && detail && activeTab === 'truck' && (
          <UnitTruckTab
            detail={detail}
            driver={driver}
            truck={truck}
            owner={owner}
            ownerrecno={ownerrecno}
            onTruckChange={onTruckChange}
            onOwnerChange={onOwnerChange}
            onOwnerRecnoChange={onOwnerRecnoChange}
            canEdit={canEdit}
            canSensitive={canSensitive}
            ownerSsn={ownerSsn}
            onOwnerSsnChange={(v) => { setOwnerSsn(v); touched(); }}
            photoUrl={truckPhotoUrl}
            portalContainer={portalContainer}
            {...childListProps}
          />
        )}
        {!loading && detail && activeTab === 'payroll' && (
          <UnitPayrollTab
            payroll={payroll}
            payrollLoading={payrollLoading}
            payrollError={payrollError}
            unitrecno={unitrecno}
            unitnumber={String(detail.unit.unitnumber ?? '')}
            portalContainer={portalContainer}
            form={payrollForm}
            onFormChange={onPayrollFormChange}
            unit={unit}
            onUnitChange={onUnitChange}
            bankEdits={bankEdits}
            onBankEdit={onBankEdit}
            onBankClear={onBankClear}
            bankEditsVersion={bankEditsVersion}
            editingBank={editingBank}
            onEditBank={() => setEditingBank(true)}
            revealed={revealed}
            onReveal={handleReveal}
            revealing={revealing}
            revealError={revealError}
            canEdit={canEdit}
            canSensitive={canSensitive}
            fieldErrors={saveErrors.fields.payroll ?? {}}
            {...childListProps}
          />
        )}
        {!loading && detail && activeTab === 'cert' && (
          <UnitCertTab
            cert={cert}
            onCertChange={onCertChange}
            canEdit={canEdit}
            portalContainer={portalContainer}
            fieldErrors={saveErrors.fields.cert ?? {}}
            {...childListProps}
          />
        )}
        {!loading && detail && activeTab === 'retention' && (
          <UnitRetentionTab
            retention={retention}
            onRetentionChange={onRetentionChange}
            canEdit={canEdit}
            fieldErrors={saveErrors.fields.retention ?? {}}
            driverrecno={detail.driver?.recno ? Number(detail.driver.recno) : null}
            onSurveysSaved={handleSurveysSaved}
            {...childListProps}
          />
        )}
        {!loading && detail && activeTab === 'documents' && (
          <div className="space-y-3">
            <div role="radiogroup" aria-label="Documents for" className="inline-flex rounded-md border border-rule p-0.5">
              {DOCUMENT_TABLES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="radio"
                  aria-checked={documentTable === t.id}
                  onClick={() => setDocumentTable(t.id)}
                  className={`rounded px-3 py-1 text-sm font-medium ${documentTable === t.id ? 'bg-accent text-accent-fg' : 'text-muted hover:bg-highlight'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <SafetyDocuments tablename={documentTable} tablerecno={documentRecno(detail, documentTable, unitrecno)} isReadOnly={!canEdit} />
          </div>
        )}
        {!loading && detail && activeTab === 'notes' && unitrecno && (
          <NotesPanel
            fetchNotes={async () => ({ notes: (await safetySystemService.listNotes('unit', unitrecno)).data ?? [] })}
            addNote={canEdit ? async (text) => { await safetySystemService.addNote('unit', unitrecno, text); } : null}
            onNoteCountChange={setNoteCount}
            isReadOnly={!canEdit}
            currentUser={currentUser}
          />
        )}
        {!loading && detail && activeTab === 'history' && unitrecno && (
          // `fetch` must be a stable reference (SafetyHistoryTab refetches when it changes).
          <SafetyHistoryTab
            recno={unitrecno}
            fetch={safetySystemService.getUnitHistory}
            labels={UNIT_HISTORY_LABELS}
            sourceLabels={UNIT_HISTORY_SOURCES}
            emptyText="No history for this unit."
          />
        )}
        {!loading && detail && activeTab === 'notifications' && unitrecno && (
          <UnitNotificationsTab unitrecno={unitrecno} />
        )}
      </div>
    </Drawer>
  );
}
